<template>
    <div>
        <b-table
                striped
                hover
                small
                responsive
                stacked="sm"
                :busy="this.$store.state.loadingPanels"
                v-bind:fields="fields"
                v-bind:items="rows"
        >
            <div slot="table-busy" class="text-center text-danger my-2">
                <b-spinner class="align-middle"/>
                <strong>&nbsp; Carregant dades...</strong>
            </div>

            <template slot="showIcons" slot-scope="row">
                <span v-if="! row.item.panel.notify"><font-awesome-icon icon="volume-mute" /></span>
                <span>&nbsp;&nbsp;</span>
                <span v-if="! row.item.panel.observe"><font-awesome-icon icon="eye-slash" /></span>
            </template>

            <template slot="showDetails" slot-scope="row">
                <b-button size="sm" @click="row.toggleDetails" class="mr-2">
                    <span><font-awesome-icon icon="info" /></span>
                </b-button>
                <b-button size="sm" class="mr-2">
                    <router-link style="color: inherit; text-decoration: none;" :to="'/readings/' + row.item.code"><span><font-awesome-icon icon="list" /></span></router-link>
                </b-button>
            </template>

            <template slot="row-details" slot-scope="row">
                <b-card>
                    <table class="table table-borderless">
                        <tbody>
                        <tr v-if="row.item.reading.power_status_code === 'error'">
                            <td class="danger-cell">Error</td>
                            <td class="base-cell">Potència</td>
                            <td class="base-cell message-cell">{{ row.item.reading.power_status_message }}</td>
                        </tr>
                        <tr v-if="row.item.reading.position_status_code === 'error'">
                            <td class="danger-cell">Error</td>
                            <td class="base-cell">Posició</td>
                            <td class="base-cell message-cell">{{ row.item.reading.position_status_message }}</td>
                        </tr>
                        <tr v-if="row.item.reading.power_status_code === 'warning'">
                            <td class="warning-cell">Alerta</td>
                            <td class="base-cell">Potència</td>
                            <td class="base-cell message-cell">{{ row.item.reading.power_status_message }}</td>
                        </tr>
                        <tr v-if="row.item.reading.position_status_code === 'warning'">
                            <td class="warning-cell">Alerta</td>
                            <td class="base-cell">Posició</td>
                            <td class="base-cell message-cell">{{ row.item.reading.position_status_message }}</td>
                        </tr>
                        </tbody>
                        <tr v-for="validation in row.item.reading.power_failed_validations">
                            <td v-if="validation.type === 'alarm'" class="danger-cell">Alarma</td>
                            <td v-if="validation.type === 'alert'" class="warning-cell">Alerta</td>
                            <td v-if="validation.type === 'info'" class="validation-cell">Info</td>
                            <td class="base-cell">Potència</td>
                            <td class="base-cell message-cell">{{ validation.message }}</td>
                        </tr>
                        <tr v-for="validation in row.item.reading.position_failed_validations">
                            <td v-if="validation.type === 'alarm'" class="danger-cell">Alarma</td>
                            <td v-if="validation.type === 'alert'" class="warning-cell">Alerta</td>
                            <td v-if="validation.type === 'info'" class="validation-cell">Info</td>
                            <td class="base-cell">Posició</td>
                            <td class="base-cell message-cell">{{ validation.message }}</td>
                        </tr>
                        <tr v-if="row.item.reading.power_p1_status_value.length > 0">
                            <td class="validation-cell">Info</td>
                            <td class="base-cell">Estat P1</td>
                            <td class="base-cell message-cell">{{ row.item.reading.power_p1_status_value }}</td>
                        </tr>
                        <tr v-if="row.item.reading.power_p2_status_value.length > 0">
                            <td class="validation-cell">Info</td>
                            <td class="base-cell">Estat P2</td>
                            <td class="base-cell message-cell">{{ row.item.reading.power_p2_status_value }}</td>
                        </tr>
                        <tr v-if="row.item.reading.power_p3_status_value.length > 0">
                            <td class="validation-cell">Info</td>
                            <td class="base-cell">Estat P3</td>
                            <td class="base-cell message-cell">{{ row.item.reading.power_p3_status_value }}</td>
                        </tr>
                        <tr v-if="row.item.reading.position_zenital_status_value.length > 0">
                            <td class="validation-cell">Info</td>
                            <td class="base-cell">Estat Zenit(Y)</td>
                            <td class="base-cell message-cell">{{ row.item.reading.position_zenital_status_value }}</td>
                        </tr>
                        <tr v-if="row.item.reading.position_azimuthal_status_value.length > 0">
                            <td class="validation-cell">Info</td>
                            <td class="base-cell">Estat Azimut(X)</td>
                            <td class="base-cell message-cell">{{ row.item.reading.position_azimuthal_status_value }}</td>
                        </tr>
                    </table>
                    <b-button :id="'show-asp-guide-' + row.item.id" v-on:click="$bvToast.show('asp-status-guide-' + row.item.id)" v-show="row.item.powerMeasurer === 'EppcPowerASP'">Guia d'estats EPPC ASP</b-button>
                    <b-toast :id="'asp-status-guide-' + row.item.id" title="Guia d'estats per EPPC ASP" no-auto-hide solid variant="info" toaster="b-toaster-bottom-full">
                        <ul>
                            <li>Per un valor d'estat, p.ex.: 00000103.</li>
                            <li>Els primer 4 digits (0000) són etiquetes de control intern, sense rellevància.</li>
                            <li>Els digits 5 i 6 (01) indiquen l'estat intern de l'ondulador. Sense rellevància.</li>
                            <li>Els dos últims digits 7 i 8 (03) expressen diferents situacions:
                                <ul>
                                    <li>00: No s'obté resposta.</li>
                                    <li>02: ASP-CAN Ok. No hi ha dades.</li>
                                    <li>03: Dades Ok.</li>
                                </ul>
                            </li>
                        </ul>
                    </b-toast>

                    <b-button :id="'show-xantrex-guide-' + row.item.id" v-on:click="$bvToast.show('xantrex-status-guide-' + row.item.id)" v-show="row.item.powerMeasurer === 'EppcPowerXantrex'">Guia d'estats EPPC Xantrex</b-button>
                    <b-toast :id="'xantrex-status-guide-' + row.item.id" title="Guia d'estats per EPPC Xantrex" no-auto-hide solid variant="info" toaster="b-toaster-bottom-full">
                        <ul>
                            <li>Per un valor d'estat, p.ex.: 00040001.</li>
                            <li>Els primer 2 digits (00) són etiquetes de control intern, sense rellevància.</li>
                            <li>Els 4 digits, del 3 fins al 6 inclosos, indiquen:
                                <ul>
                                    <li>0400: Invert</li>
                                    <li>0401: AC Passthru</li>
                                    <li>0402: APS Only</li>
                                    <li>0403: Load Sense</li>
                                    <li>0404: Inverter Disabled</li>
                                    <li>0405: Load Sense Ready</li>
                                    <li>0406: Engaging Inverter</li>
                                    <li>0407: Invert Fault</li>
                                    <li>0408: Inverter Standby</li>
                                    <li>0409: Grid-Tied</li>
                                </ul>
                            </li>
                            <li>Els dos últims digits 7 i 8 (01) indiquen:
                                <ul>
                                    <li>00: No hi ha ondulador</li>
                                    <li>01: Dades Ok</li>
                                </ul>
                            </li>
                        </ul>
                    </b-toast>

                    <b-button :id="'show-modicon-guide-' + row.item.id" v-on:click="$bvToast.show('modicon-status-guide-' + row.item.id)" v-show="row.item.positionMeasurer === 'Modicon'">Guia d'estats Modicon</b-button>
                    <b-toast :id="'modicon-status-guide-' + row.item.id" title="Guia d'estats per MODICON" no-auto-hide solid variant="info" toaster="b-toaster-bottom-full">
                        <ul>
                            <li>bit 0: '0' ok; '1' Alarma de posició detectada per la pantalla (no és possible situar-se en la posició teòrica)</li>
                            <li>bit 1: '0' ok; '1' Alarma de vent. Ho informa la pantalla.</li>
                            <li>bit 2: '0' ok; '1' Alarma de comunicació entre PLC màster i PLC slave(pantalla). Ho informa la pantalla.</li>
                            <li>bit 3: '0' ok; '1' Alarma de vent, ho informa el màster. Quan es produeixi una alarma de vent, qui té la informació és el màster i
                                la passa a slave (PLC pantalla) per tant tant el bit 1 com el 3 s’haurien d’activar.</li>
                            <li>bit 4: '0' ok; '1' alarma de comunicació, detectada pel màster.  Quan es produeix una alarma de comunicació entre el màster i un slave (pantalla),
                                el més normal és que sigui el màster el que detecti aquest error i no tinguem informació de l’slave (ja que no hi tenim comunicació),
                                però s’ha posar també l’alarma de comunicació de slave per si ens pot aportar més informació.</li>
                            <li>bit 5: '0' mode manual; '1' mode de la planta en automàtic.</li>
                        </ul>
                    </b-toast>
                </b-card>
            </template>

            <template v-if="editable" slot="actions" slot-scope="row">
                <b-button size="sm" @click="edit(row.item)" class="mr-2">
                    <span><font-awesome-icon icon="edit" /></span>
                </b-button>
            </template>
        </b-table>

        <b-modal
                ref="panelEditor"
                title="Editar seguidor"
                header-bg-variant="info"
                header-text-variant="light"
                footer-bg-variant="light"
                footer-text-variant="dark"
                style="width: 600px !important;"
        >
            <b-container fluid>
                <b-form>
                    <b-form-group id="panelCodeGroup" label="Codi:" label-for="panelCode">
                        <b-form-input :disabled="savingPanel" id="panelCode" type="text" v-model="editingPanel.code" requuired placeholder="Introdueix el codi"></b-form-input>
                    </b-form-group>
                    <b-form-group id="panelDescriptionGroup" label="Descripció:" label-for="panelDescription">
                        <b-form-input :disabled="savingPanel" id="panelDescription" type="text" v-model="editingPanel.description" required placeholder="Introdueix la descripció"></b-form-input>
                    </b-form-group>

                    <br/>
                    <hr/>

                    <!-- Position measurer params -->
                    <b-form-group id="posMeasurerTypeGroup" label="Mesurador de posició:" label-for="positionMeasurerType">
                        <b-form-select :disabled="savingPanel" v-model="editingPanel.position_measurer_type" id="positionMeasurerType" :options="positionMeasurers"/>
                    </b-form-group>
                    <b-form-group
                            v-if="editingPanel.position_measurer_type === 'EppcPosition'"
                            id="positionEppcUrlGroup" label="Url:" label-for="positionEppcUrl">
                        <b-form-input :disabled="savingPanel" id="positionEppcUrl" type="text" v-model="editingPanel.position_measurer_params.baseUrl" required placeholder="URL base del mesurador">
                        </b-form-input>
                    </b-form-group>
                    <b-form-group
                            v-if="editingPanel.position_measurer_type === 'EppcPosition'"
                            id="positionEppcMoverGroup" label="Nº Node/Mover:" label-for="positionEppcMoverId">
                        <b-form-input :disabled="savingPanel" id="positionEppcMoverId" type="text" v-model="editingPanel.position_measurer_params.moverId" required placeholder="Nº de mover/node">
                        </b-form-input>
                    </b-form-group>
                    <b-form-group
                            v-if="editingPanel.position_measurer_type === 'Modicon'"
                            id="positionModiconIpGroup" label="IP:" label-for="positionModiconIp">
                        <b-form-input :disabled="savingPanel" id="positionModiconIp" type="text" v-model="editingPanel.position_measurer_params.ip" required placeholder="IP del mesurador">
                        </b-form-input>
                    </b-form-group>
                    <b-form-group
                            v-if="editingPanel.position_measurer_type === 'Modicon'"
                            id="positionModiconPortGroup" label="Port:" label-for="positionModiconPort">
                        <b-form-input :disabled="savingPanel" id="positionModiconPort" type="text" v-model="editingPanel.position_measurer_params.port" required placeholder="Port de connexió">
                        </b-form-input>
                    </b-form-group>
                    <b-form-group
                            v-if="editingPanel.position_measurer_type === 'Modicon'"
                            id="positionModiconStartRegisterGroup" label="Registre inicial:" label-for="positionModiconStartRegister">
                        <b-form-input :disabled="savingPanel" id="positionModiconStartRegister" type="number" v-model="editingPanel.position_measurer_params.startRegister" required placeholder="Registre inicial">
                        </b-form-input>
                    </b-form-group>
                    <b-form-group
                            id="positionAlertThresholdGroup" label="% Alerta:" label-for="positionAlertThreshold">
                        <b-form-input :disabled="savingPanel" id="positionAlertThreshold" type="number" v-model="editingPanel.position_alert_threshold" required placeholder="% per alerta">
                        </b-form-input>
                    </b-form-group>
                    <b-form-group
                            id="positionAlarmThresholdGroup" label="% Alarma:" label-for="positionAlarmThreshold">
                        <b-form-input :disabled="savingPanel" id="positionAlarmThreshold" type="number" v-model="editingPanel.position_alarm_threshold" required placeholder="% per alarma">
                        </b-form-input>
                    </b-form-group>

                    <br/>
                    <hr/>

                    <!-- Poser measurer params -->
                    <b-form-group id="powMeasurerTypeGroup" label="Mesurador de potència:" label-for="powerMeasurerType">
                        <b-form-select :disabled="savingPanel" v-model="editingPanel.power_measurer_type" id="powerMeasurerType" :options="powerMeasurers"/>
                    </b-form-group>
                    <b-form-group
                            v-if="editingPanel.power_measurer_type === 'EppcPowerASP' || editingPanel.power_measurer_type === 'EppcPowerXantrex'"
                            id="powerEppcUrlGroup" label="Url:" label-for="powerEppcUrl">
                        <b-form-input :disabled="savingPanel" id="powerEppcUrl" type="text" v-model="editingPanel.power_measurer_params.baseUrl" required placeholder="URL base del mesurador">
                        </b-form-input>
                    </b-form-group>
                    <b-form-group
                            v-if="editingPanel.power_measurer_type === 'EppcPowerASP' || editingPanel.power_measurer_type === 'EppcPowerXantrex'"
                            id="powerEppcMoverGroup" label="Nº Node/Mover:" label-for="powerEppcMoverId">
                        <b-form-input :disabled="savingPanel" id="powerEppcMoverId" type="text" v-model="editingPanel.power_measurer_params.moverId" required placeholder="Nº de mover/node">
                        </b-form-input>
                    </b-form-group>
                    <b-form-group
                            v-if="editingPanel.power_measurer_type === 'Fronius' || editingPanel.power_measurer_type === 'GenericModbus'"
                            id="powerFroniusIpGroup" label="IP:" label-for="powerFroniusIp">
                        <b-form-input :disabled="savingPanel" id="powerFroniusIp" type="text" v-model="editingPanel.power_measurer_params.ip" required placeholder="IP del mesurador">
                        </b-form-input>
                    </b-form-group>
                    <b-form-group
                            v-if="editingPanel.power_measurer_type === 'Fronius' || editingPanel.power_measurer_type === 'GenericModbus'"
                            id="powerFroniusPortGroup" label="Port:" label-for="powerFroniusPort">
                        <b-form-input :disabled="savingPanel" id="powerFroniusPort" type="text" v-model="editingPanel.power_measurer_params.port" required placeholder="Port de connexió">
                        </b-form-input>
                    </b-form-group>
                    <b-form-group
                            v-if="editingPanel.power_measurer_type === 'GenericModbus'"
                            id="powerGenericRegisterGroup" label="Registre Modbus:" label-for="powerGenericRegister">
                        <b-form-input :disabled="savingPanel" id="powerGenericRegister" type="number" v-model="editingPanel.power_measurer_params.register" required placeholder="Ex: 32080 per Huawei">
                        </b-form-input>
                    </b-form-group>
                    <b-form-group
                            v-if="editingPanel.power_measurer_type === 'GenericModbus'"
                            id="powerGenericDataTypeGroup" label="Tipus de dada:" label-for="powerGenericDataType">
                        <b-form-select :disabled="savingPanel" id="powerGenericDataType" v-model="editingPanel.power_measurer_params.dataType">
                            <option value="Float">Decimal (Fronius / Float32)</option>
                            <option value="Int32">Enter (Huawei / Int32)</option>
                        </b-form-select>
                    </b-form-group>
                    <b-form-group
                            id="powerTypeGroup" label="Tipus potència:" label-for="powerType">
                        <b-form-select :disabled="savingPanel" id="powerType" v-model="editingPanel.power_type">
                            <option>6.75kW</option>
                            <option>9kW</option>
                        </b-form-select>
                    </b-form-group>
                    <b-form-group
                            id="powerAlertThresholdGroup" label="% Alerta:" label-for="powerAlertThreshold">
                        <b-form-input :disabled="savingPanel" id="powerAlertThreshold" type="number" v-model="editingPanel.power_alert_threshold" required placeholder="% per alerta">
                        </b-form-input>
                    </b-form-group>
                    <b-form-group
                            id="powerAlarmThresholdGroup" label="% Alarma:" label-for="powerAlarmThreshold">
                        <b-form-input :disabled="savingPanel" id="powerAlarmThreshold" type="number" v-model="editingPanel.power_alarm_threshold" required placeholder="% per alarma">
                        </b-form-input>
                    </b-form-group>

                    <br/>
                    <hr/>

                    <b-form-group id="panelLatitudeGroup" label="Latitud:" label-for="panelLatitude">
                        <b-form-input :disabled="savingPanel" id="panelLatitude" v-model="editingPanel.latitude" type="number" step="0.000001" required placeholder="Introdueix la latitud"></b-form-input>
                    </b-form-group>
                    <b-form-group id="panelLongitudeGroup" label="Longitud:" label-for="panelLongitude">
                        <b-form-input :disabled="savingPanel" id="panelLongitude" v-model="editingPanel.longitude" type="number" step="0.000001" required placeholder="Introdueix la longitud"></b-form-input>
                    </b-form-group>
                    <b-form-group id="panelNotifyGroup">
                        <b-form-checkbox :disabled="savingPanel" :id="'panelNotify'+editingPanel.id" v-model="editingPanel.notify">Activar les notificacions en cas d'anomalies.</b-form-checkbox>
                    </b-form-group>
                    <b-form-group id="panelObserveGroup">
                        <b-form-checkbox :disabled="savingPanel" :id="'panelObserve'+editingPanel.id" v-model="editingPanel.observe">Recaptar lectures d'aquest seguidor.</b-form-checkbox>
                    </b-form-group>

                    <b-alert variant="danger" v-show="this.saveError" show>
                        {{ this.saveError }}
                    </b-alert>
                </b-form>
            </b-container>

            <div slot="modal-footer" class="w-100">
                <b-button :disabled="savingPanel" class="float-left" variant="warning" @click="closePanelEditor">Cancelar</b-button>
                <b-button :disabled="savingPanel" class="float-right" variant="info" @click="savePanel">
                    {{ savingPanel ? 'Guardant...' : 'Guardar' }}
                    <span v-show="savingPanel">&nbsp;&nbsp;</span><b-spinner small v-show="savingPanel" label="Actualitzant..." />
                </b-button>
            </div>
        </b-modal>
    </div>
</template>

<style scoped>
    .danger-cell {
        background-color: #f5c6cb;
    }

    .warning-cell {
        background-color: #ffeeba;
    }

    .validation-cell {
        background-color: #bee5eb;
    }

    .message-cell {
        text-align: left;
    }

    .base-cell {
        background-color: white;
    }
</style>

<script>
    import Api from "../api"
    import router from "../router"

    export default {
        name: "PanelsTable",
        components: {},
        props: {
            panels: Array,
        },
        methods: {
            edit(item) {
                //ToDo: Solve editing cancel refresh on panels table. Not reverting row values.
                this.editingItem = item
                this.editingPanel = item.panel
                this.originalEditingItem = JSON.stringify(item)
                this.originalEditingPanel = JSON.stringify(item.panel)
                window.console.log(this.editingPanel)
                this.$refs.panelEditor.show()
            },
            savePanel() {
                this.saveError = null
                this.savingPanel = true
                //Create a post request to panel resource using data filled on editingPanel:
                Api.updatePanel(this.editingPanel).then(() => {
                    //Updated succefull. Close modal.
                    this.savingPanel = false
                    this.$refs.panelEditor.hide()
                }).catch( (error)  => {
                    if (error.response && error.response.status === 401) {
                        //Unauthorized:
                        router.push('/login');
                        return;
                    }

                    this.saveError = "S'ha produit un error al intentar guardar les dades: " + error.message
                    if (error.response && error.response.data && error.response.data.message) {
                        this.saveError = "S'ha produit un error al intentar guardar les dades: " + error.response.data.message
                    }
                    window.console.log(error.response)
                    window.console.log(this.saveError)
                    this.savingPanel = false
                })
            },
            closePanelEditor() {
                this.editingPanel = JSON.parse(this.originalEditingPanel)
                this.editingItem = JSON.parse(this.originalEditingItem)
                this.$refs.panelEditor.hide()
            }
        },
        data: function () {
            return {
                savingPanel: false,
                saveError: null,
                editingPanel: {
                    code: null,
                    description: null,
                    position_measurer_type: null,
                    position_measurer_params: null,
                    power_measurer_type: null,
                    power_measurer_params: null,
                    latitude: null,
                    longitude: null,
                    notify: null,
                    observe: null,
                },
                editingItem: null,
                originalEditingPanel: null,
                originalEditingItem: null,
                positionMeasurers: ['EppcPosition', 'Modicon'],
                powerMeasurers: ['EppcPowerASP', 'EppcPowerXantrex', 'Fronius', 'GenericModbus'],
            }
        },
        computed: {
            editable() {
                return this.$store.state.user.can_edit;
            },
            fields() {
                let fields = {
                    code: {
                        label: 'Codi',
                        sortable: true
                    },
                    description: {
                        label: 'Descripció',
                        sortable: true,
                    },
                    powerMeasurer: {
                        label: 'Mesurador',
                        sortable: true,
                    },
                    powerType: {
                        label: 'Tipus',
                        sortable: true,
                    },
                    power1: {
                        label: 'Potència (P1)',
                        sortable: true
                    },
                    power2: {
                        label: 'Potència (P2)',
                        sortable: true
                    },
                    power3: {
                        label: 'Potència (P3)',
                        sortable: true
                    },
                    positionMeasurer: {
                        label: 'Mesurador',
                        sortable: true
                    },
                    zenit: {
                        label: 'Zenit(Y)',
                        sortable: true
                    },
                    azimuth: {
                        label: 'Azimut(X)',
                        sortable: true
                    },
                    registeredAt: {
                        label: 'Última lectura',
                        sortable: true
                    },
                    showIcons: {
                        label: '',
                    },
                    showDetails: {
                        label: 'Detalls'
                    }
                }
                if (this.editable) {

                    fields.actions = {
                        label: 'Accions'
                    }
                }
                return fields;
            },
            rows() {
                //Apply styling for warnigs and other possible item related information to display on table:
                let rows = []
                this.panels.forEach(panel => {
                    let row = {
                        id: panel.id,
                        code: panel.code,
                        description: panel.description,
                        powerMeasurer: panel.power_measurer_type,
                        powerType: panel.power_type,
                        power1: panel.last_reading.power_p1,
                        power2: panel.last_reading.power_p2,
                        power3: panel.last_reading.power_p3,
                        positionMeasurer: panel.position_measurer_type,
                        zenit: panel.last_reading.position_zenital_angle,
                        azimuth: panel.last_reading.position_azimuthal_angle,
                        registeredAt: panel.last_reading.registered_at,
                        panel: panel,
                        reading: panel.last_reading,
                        _cellVariants: {}
                    }

                    let powerVariantDefined = false
                    if (row.reading.power_status_code === 'warning') {
                        row._cellVariants.power1 = 'warning'
                        row._cellVariants.power2 = 'warning'
                        row._cellVariants.power3 = 'warning'
                        powerVariantDefined = true
                    }
                    if (row.reading.power_status_code === 'error') {
                        row._cellVariants.power1 = 'danger'
                        row._cellVariants.power2 = 'danger'
                        row._cellVariants.power3 = 'danger'
                        powerVariantDefined = true
                    }

                    let positionVariantDefined = false
                    if (row.reading.position_status_code === 'warning') {
                        row._cellVariants.zenit = 'warning'
                        row._cellVariants.azimuth = 'warning'
                        positionVariantDefined = true
                    }
                    if (row.reading.position_status_code === 'error') {
                        row._cellVariants.zenit = 'danger'
                        row._cellVariants.azimuth = 'danger'
                        positionVariantDefined = true
                    }

                    if (row.reading.power_failed_validations.length > 0 && !powerVariantDefined) {
                        let alerts = 0;
                        let alarms = 0;
                        row.reading.power_failed_validations.forEach(function (validation) {
                            if (validation.type === 'alarm') {
                                alarms++
                            }
                            if (validation.type === 'alert') {
                                alerts++
                            }
                        })
                        if (alarms>0) {
                            row._cellVariants.power1 = 'danger'
                            row._cellVariants.power2 = 'danger'
                            row._cellVariants.power3 = 'danger'
                        } else if (alerts>0) {
                            row._cellVariants.power1 = 'warning'
                            row._cellVariants.power2 = 'warning'
                            row._cellVariants.power3 = 'warning'
                        } else {
                            row._cellVariants.power1 = 'info'
                            row._cellVariants.power2 = 'info'
                            row._cellVariants.power3 = 'info'
                        }
                    }
                    if (row.reading.position_failed_validations.length > 0 && !positionVariantDefined) {
                        let alerts = 0;
                        let alarms = 0;
                        row.reading.position_failed_validations.forEach(function (validation) {
                            if (validation.type === 'alarm') {
                                alarms++
                            }
                            if (validation.type === 'alert') {
                                alerts++
                            }
                        })
                        if (alarms>0) {
                            row._cellVariants.zenit = 'danger'
                            row._cellVariants.azimuth = 'danger'
                        } else if (alerts>0) {
                            row._cellVariants.zenit = 'warning'
                            row._cellVariants.azimuth = 'warning'
                        } else {
                            row._cellVariants.zenit = 'info'
                            row._cellVariants.azimuth = 'info'
                        }
                    }

                    rows.push(row)
                })
                return rows
            }
        }
    }
</script>